/* Mats · Quick view global
   Abre con cualquier [data-mats-quick-view="URL"] (lo gestiona mats-product-card.js).
   Pide el contenido con la Section Rendering API: URL?section_id=mats-quick-view
   Depende de window.MatsCart / window.MatsWishlist (mats-product-card.js). */
(() => {
  if (customElements.get('mats-quick-view')) return;

  const SECTION_ID = 'mats-quick-view';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class MatsQuickView extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-mqv-dialog]');
      this.body = this.querySelector('[data-mqv-body]');
      this.loadingHTML = this.body.innerHTML;
      this.cache = new Map();

      this.querySelector('[data-mqv-close]').addEventListener('click', () => this.close());
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        if (this.guideOpen) this.toggleGuide(false);
        else this.close();
      });
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      this.body.addEventListener('click', (e) => this.onBodyClick(e));

      // Precarga al pasar el ratón / tocar
      this._prefetch = (e) => {
        const t = e.target.closest && e.target.closest('[data-mats-quick-view]');
        if (t) this.fetchContent(t.dataset.matsQuickView).catch(() => {});
      };
      document.addEventListener('pointerover', this._prefetch, { passive: true });
      document.addEventListener('touchstart', this._prefetch, { passive: true });

      this._onWish = () => this.syncSaveLabel();
      document.addEventListener('mats:wishlist:change', this._onWish);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerover', this._prefetch);
      document.removeEventListener('touchstart', this._prefetch);
      document.removeEventListener('mats:wishlist:change', this._onWish);
    }

    /* ---------- Abrir / cerrar ---------- */

    async openFor(url, trigger) {
      this.trigger = trigger || null;
      trigger?.setAttribute('aria-expanded', 'true');
      this.body.innerHTML = this.loadingHTML;
      if (!this.dialog.open) {
        this.dialog.classList.remove('is-closing');
        this.dialog.showModal();
        document.documentElement.classList.add('mats-qv-lock');
      }
      try {
        const html = await this.fetchContent(url);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const tpl = doc.querySelector('[data-mqv-template]');
        if (!tpl) throw new Error('Quick view vacío');
        this.body.replaceChildren(tpl.content.cloneNode(true));
        this.init();
        window.MatsSizeChart?.init(this.body); // tabla de medidas Mats del panel «Guía de tallas»
      } catch (err) {
        console.warn('[Mats] Quick view:', err.message);
        window.location.href = url; // si algo falla, a la ficha
      }
    }

    fetchContent(url) {
      const clean = url.split('#')[0];
      if (!this.cache.has(clean)) {
        const sep = clean.includes('?') ? '&' : '?';
        const p = fetch(`${clean}${sep}section_id=${SECTION_ID}`, { credentials: 'same-origin' })
          .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
          .catch((e) => { this.cache.delete(clean); throw e; });
        this.cache.set(clean, p);
      }
      return this.cache.get(clean);
    }

    close() {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      const finish = () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        document.documentElement.classList.remove('mats-qv-lock');
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.trigger?.focus({ preventScroll: true });
      };
      if (reduceMotion.matches) { finish(); return; }
      this.dialog.classList.add('is-closing');
      this.dialog.addEventListener('animationend', finish, { once: true });
      setTimeout(() => { if (this.dialog.open) finish(); }, 400); // seguridad
    }

    /* ---------- Estado de variantes ---------- */

    init() {
      this.guideOpen = false;
      this.dialog.classList.remove('is-guide-open');
      const json = this.body.querySelector('[data-mqv-json]');
      this.data = JSON.parse(json.textContent);
      this.variants = this.data.variants;
      const n = this.variants[0]?.options.length || 0;

      this.fieldsets = [...this.body.querySelectorAll('[data-mqv-option]')];
      this.selected = new Array(n).fill(null);
      // Opciones con un único valor: fijas
      for (let i = 0; i < n; i++) {
        const values = new Set(this.variants.map((v) => v.options[i]));
        if (values.size === 1) this.selected[i] = this.variants[0].options[i];
      }
      this.fieldsets.forEach((fs) => {
        const i = Number(fs.dataset.mqvOption);
        if (fs.dataset.selected) this.selected[i] = fs.dataset.selected;
      });

      this.img = this.body.querySelector('.mats-qv__img');
      this.priceNow = this.body.querySelector('[data-mqv-price-now]');
      this.priceWas = this.body.querySelector('[data-mqv-price-was]');
      this.discount = this.body.querySelector('[data-mqv-discount]');
      this.avail = this.body.querySelector('[data-mqv-availability]');
      this.addBtn = this.body.querySelector('[data-mqv-add]');
      this.addLabel = this.body.querySelector('[data-mqv-add-label]');

      window.MatsWishlist?.sync(this.body);
      this.syncSaveLabel();
      this.update();

      const first = this.body.querySelector('.mats-qv__value') || this.addBtn;
      first?.focus({ preventScroll: true });
    }

    get currentVariant() {
      if (this.selected.some((v) => v === null)) return null;
      return this.variants.find((v) => v.options.every((o, i) => o === this.selected[i])) || null;
    }

    update() {
      // Valores sin stock según el resto de la selección
      this.fieldsets.forEach((fs) => {
        const i = Number(fs.dataset.mqvOption);
        fs.querySelectorAll('[data-mqv-value]').forEach((btn) => {
          const val = btn.dataset.mqvValue;
          const ok = this.variants.some((v) => v.available && v.options[i] === val &&
            v.options.every((o, k) => k === i || this.selected[k] === null || this.selected[k] === o));
          btn.classList.toggle('is-unavailable', !ok);
          btn.setAttribute('aria-pressed', String(this.selected[i] === val));
        });
      });

      const v = this.currentVariant;
      const qv = this.dataset;

      if (!v) {
        if (this.avail && !this.avail.classList.contains('is-error')) {
          this.avail.className = 'mats-qv__availability';
          this.avail.textContent = qv.labelInitial;
        }
        if (this.addBtn && this.variants.some((x) => x.available)) {
          this.addBtn.disabled = false;
          this.addLabel.textContent = qv.labelAdd;
        }
        return;
      }

      // Precio
      this.priceNow.textContent = v.price;
      this.priceNow.classList.toggle('is-sale', !!v.compare);
      this.priceWas.hidden = !v.compare;
      if (v.compare) this.priceWas.textContent = v.compare;
      this.discount.hidden = !v.discount;
      if (v.discount) this.discount.textContent = `−${v.discount}%`;

      // Imagen de variante
      if (v.image && this.img && !this.img.src.includes(v.image.replace(/^https?:/, ''))) {
        this.img.classList.add('is-swapping');
        const src = v.image.startsWith('//') ? `https:${v.image}` : v.image;
        const pre = new Image();
        pre.onload = () => {
          this.img.removeAttribute('srcset');
          this.img.src = src;
          this.img.classList.remove('is-swapping');
        };
        pre.src = src;
      }

      // Disponibilidad
      const threshold = Number(qv.lowStockThreshold || 0);
      let cls = 'is-ok';
      let txt = qv.labelInStock;
      if (!v.available) {
        cls = 'is-out';
        txt = qv.labelSoldOut;
      } else if (v.qty !== null && v.qty > 0 && v.qty <= threshold) {
        cls = 'is-low';
        txt = qv.labelLowStock.replace('[count]', v.qty);
      }
      this.avail.className = `mats-qv__availability ${cls}`;
      this.avail.textContent = txt;

      this.addBtn.disabled = !v.available;
      this.addLabel.textContent = v.available ? qv.labelAdd : qv.labelUnavailable;
    }

    /* ---------- Clicks ---------- */

    onBodyClick(e) {
      const valueBtn = e.target.closest('[data-mqv-value]');
      if (valueBtn) {
        const fs = valueBtn.closest('[data-mqv-option]');
        const i = Number(fs.dataset.mqvOption);
        this.selected[i] = valueBtn.dataset.mqvValue;
        fs.classList.remove('is-invalid');
        this.avail?.classList.remove('is-error');
        this.update();
        return;
      }

      // «Elegir talla X» de la tabla de medidas: la marca aquí y vuelve al producto
      const pickBtn = e.target.closest('[data-mb-sc-pick]');
      if (pickBtn && pickBtn.dataset.size) { this.pickSize(pickBtn.dataset.size); return; }

      if (e.target.closest('[data-mqv-guide-open]')) { this.toggleGuide(true); return; }
      if (e.target.closest('[data-mqv-guide-close]')) { this.toggleGuide(false); return; }

      if (e.target.closest('[data-mqv-add]')) this.add();
    }

    sizeFieldset() {
      return this.body.querySelector('.mats-qv__option--size') || null;
    }

    pickSize(size) {
      const fs = this.sizeFieldset();
      const key = String(size).trim().toLowerCase();
      const btn = fs && [...fs.querySelectorAll('[data-mqv-value]')].find((b) => b.dataset.mqvValue.trim().toLowerCase() === key);
      if (btn) btn.click();
      this.toggleGuide(false);
    }

    toggleGuide(open) {
      const panel = this.body.querySelector('[data-mqv-guide]');
      const opener = this.body.querySelector('[data-mqv-guide-open]');
      if (!panel) return;
      this.guideOpen = open;
      if (open) {
        this.dialog.scrollTop = 0;
        panel.scrollTop = 0;
        // La tabla arranca en la talla elegida (si hay)
        const fs = this.sizeFieldset();
        const chart = panel.querySelector('[data-mb-sc]')?.__matsSizeChart;
        if (fs && chart) {
          const i = Number(fs.dataset.mqvOption);
          if (this.selected[i]) chart.setBySize(this.selected[i], false);
        }
      }
      panel.hidden = !open;
      this.dialog.classList.toggle('is-guide-open', open);
      opener?.setAttribute('aria-expanded', String(open));
      if (open) panel.querySelector('[data-mqv-guide-close]')?.focus({ preventScroll: true });
      else opener?.focus({ preventScroll: true });
    }

    async add() {
      const v = this.currentVariant;
      if (!v) {
        const missing = this.fieldsets.filter((fs) => this.selected[Number(fs.dataset.mqvOption)] === null);
        missing.forEach((fs) => fs.classList.add('is-invalid'));
        this.avail.className = 'mats-qv__availability is-error';
        this.avail.textContent = this.dataset.labelChoose;
        this.avail.setAttribute('role', 'alert');
        missing[0]?.querySelector('[data-mqv-value]')?.focus();
        return;
      }
      if (!v.available || !window.MatsCart) return;

      this.addBtn.classList.add('is-loading');
      try {
        await window.MatsCart.add(v.id);
        window.MatsCart.announce(this.dataset.labelAdded);
        const mode = this.dataset.afterAdd;
        if (mode === 'stay') {
          this.addLabel.textContent = this.dataset.labelAdded;
          setTimeout(() => this.update(), 1600);
        } else if (mode === 'cart') {
          window.MatsCart.afterAdd('cart');
        } else {
          this.close();
          if (mode === 'drawer') setTimeout(() => window.MatsCart.afterAdd('drawer'), 220);
        }
      } catch (err) {
        this.avail.className = 'mats-qv__availability is-error';
        this.avail.textContent = err.message;
      } finally {
        this.addBtn.classList.remove('is-loading');
      }
    }

    syncSaveLabel() {
      const btn = this.body?.querySelector('.mats-qv__save');
      if (!btn) return;
      const saved = window.MatsWishlist?.has(btn.dataset.matsWish);
      btn.setAttribute('aria-pressed', String(!!saved));
      const span = btn.querySelector('span');
      if (span) span.textContent = saved ? this.dataset.labelSaved : this.dataset.labelSave;
    }
  }

  customElements.define('mats-quick-view', MatsQuickView);
})();
